import type { ReactNode } from 'react'
import { GridBar } from './GridBar'
import { GridSelection } from './grid/GridCommandBar'
import s from './CollectionWorkspace.module.css'

export interface CollectionWorkspaceSelection {
  /** Selected DATA rows in this grid instance (never group headers). */
  count: number
  /** "Actions for selected products". */
  label: string
  onClear: () => void
  /** Governed batch actions (`cardinality = 'batch'`), already authorised. */
  actions?: ReactNode
}

export interface CollectionWorkspaceProps {
  /** Stable logical identity; never a parent record UUID. */
  identity: string
  /** 0 for a root collection; +1 for every relationship level beneath it. */
  depth?: number
  onCommand?: () => void
  commandLabel?: string
  /** Governed command buttons beside the mark, when the preference shows them. */
  commands?: ReactNode
  /** From the first selected row, replaces `commands` in place. */
  selection?: CollectionWorkspaceSelection
  /** `GridSearch` — present on every interactive grid. */
  search?: ReactNode
  /** Present ONLY while the view is modified; renders "Save as…". */
  onSaveAs?: () => void
  /** The saved-view selector. */
  viewSelector?: ReactNode
  /** The view-settings ⋮ (`ViewSettingsMenu`). */
  viewSettings?: ReactNode
  /** `GridFilterPills` — renders nothing when no filter applies. */
  filters?: ReactNode
  /**
   * An honest statement about what this collection could not do as asked —
   * e.g. a saved view grouping by a field this adapter cannot group exactly
   * (ADR F-84 §2). Drawn between the bar and the header, never as a toast.
   */
  notice?: ReactNode
  /** The relationship tabs a nested collection hangs from (`RelationshipTabStrip`). */
  tabs?: ReactNode
  footer?: ReactNode
  children: ReactNode
  testId?: string
}

/**
 * The complete chrome around a record collection (#2375, the approved unified
 * grid) — the same composition for a root list, a form relationship, an inline
 * level-1 band and a recursive level-2 band. Depth changes DENSITY only.
 *
 *   BAR     mark · commands|selection ········ search · [Save as…] · view ▾ · ⋮
 *   PILLS   (only when filters apply)
 *   HEADER / ROWS / BAND   the host's DataGrid (children)
 *   FOOTER  count ········ pager (depth 0) | count (depth > 0)
 *
 * The controls band (Filter / Columns / Sort / Save view / More) and its count
 * badges are gone — the column ⋮ and the view-settings ⋮ carry those verbs —
 * and so is the bar's title: the page header owns a root collection's title,
 * and a nested collection's view selector names its relationship.
 *
 * It is deliberately data-free: the host owns queries, saved-view drafts,
 * authority and mutations. Every band starts at `--g-inset`, which is what
 * lines the mark, the select-all and every row checkbox up on one axis.
 */
export function CollectionWorkspace({
  identity, depth = 0, onCommand, commandLabel, commands, selection, search, onSaveAs,
  viewSelector, viewSettings, filters, notice, tabs, footer, children,
  testId = 'collection-workspace',
}: CollectionWorkspaceProps) {
  const dense = depth > 0
  const selectionGroup = selection && selection.count > 0 ? (
    <GridSelection count={selection.count} label={selection.label} onClear={selection.onClear}>
      {selection.actions}
    </GridSelection>
  ) : undefined
  const saveAs = onSaveAs ? (
    <button type="button" className={s.saveAs} data-testid="grid-save-as" onClick={onSaveAs}>Save as…</button>
  ) : undefined
  const section = (
    <section className={`${s.root}${dense ? ` ${s.dense}` : ''}`}
      data-testid={testId} data-collection-identity={identity} data-collection-depth={depth}
      data-tabbed={tabs != null || undefined}>
      <GridBar dense={dense}
        testId={`${testId}-bar`}
        onCommand={onCommand}
        commandLabel={commandLabel}
        commands={commands}
        selection={selectionGroup}
        search={search}
        saveAs={saveAs}
        views={viewSelector}
        settings={viewSettings} />
      {filters}
      {notice ? <div className={s.notice} role="status" data-testid={`${testId}-notice`}>{notice}</div> : null}
      <div className={s.canvas} data-testid={`${testId}-canvas`}>{children}</div>
      {footer ? <footer className={s.footer} data-testid="grid-footer">{footer}</footer> : null}
    </section>
  )
  if (tabs == null) return section
  return (
    <div className={s.tabbed} data-collection-depth={depth}>
      {tabs}
      {section}
    </div>
  )
}
